import React, { useState } from "react";
import { Briefcase, ArrowRight } from "lucide-react";
import { Typography, Button, CustomInput, Loading } from "../common";
import { useToast } from "../../context/CustomToast";
import { useUpdateJobTitleMutation } from "../../store/api/postgresUserApi";

interface JobTitleModalProps {
  open: boolean;
  onSubmit: () => void;
  userName?: string;
}

/**
 * Mandatory blocking modal for legacy users who need to provide their job title.
 * This modal cannot be dismissed - user must provide a job title to proceed.
 */
const JobTitleModal: React.FC<JobTitleModalProps> = ({
  open,
  onSubmit,
  userName,
}) => {
  const toast = useToast();
  const [jobTitle, setJobTitle] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [updateJobTitle, { isLoading }] = useUpdateJobTitleMutation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setJobTitle(e.target.value);
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = jobTitle.trim();
    if (!trimmed) {
      setError("Please enter your job title");
      return;
    }

    if (trimmed.length < 2) {
      setError("Job title must be at least 2 characters");
      return;
    }

    try {
      await updateJobTitle({ job_title: trimmed }).unwrap();
      toast.showToast("Job title saved successfully!", "success");
      onSubmit();
    } catch (err) {
      console.error("Failed to update job title:", err);
      setError("Failed to save job title. Please try again.");
      toast.showToast("Failed to save job title", "error");
    }
  };

  if (!open) return null;

  const displayName = userName?.split(" ")[0] || "there";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-primary p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Briefcase size={24} />
            </div>
            <Typography variant="h4" weight="bold" className="!text-white">
              One more thing...
            </Typography>
          </div>
          <Typography variant="p" className="!text-white/90">
            Hi {displayName}! To personalise your learning experience, please
            tell us your job title.
          </Typography>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <CustomInput
            label="Your Job Title"
            name="jobTitle"
            type="text"
            placeholder="e.g., Marketing Manager, Software Engineer"
            value={jobTitle}
            onChange={handleChange}
            error={!!error}
            helperText={error || undefined}
            required
            autoFocus
            icon={Briefcase}
            className={{
              label: "text-small font-medium",
              input: "text-p h-11",
            }}
          />

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <Typography variant="small" className="text-blue-800">
              Your job title helps us recommend the most relevant courses and
              learning paths for your role.
            </Typography>
          </div>

          <Button
            label={isLoading ? "Saving..." : "Continue to Dashboard"}
            variant="filled"
            className="w-full bg-primary text-white font-bold h-12"
            endIcon={
              isLoading ? (
                <Loading />
              ) : (
                <ArrowRight size={20} />
              )
            }
            disabled={isLoading || !jobTitle.trim()}
            buttonType="submit"
          />
        </form>
      </div>
    </div>
  );
};

export default JobTitleModal;
